import "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/layerContent.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import LayerSettings from "./layerSettings/LayerSettings";
import {
  defaultLineStyle,
  defaultPointStyle,
  defaultPolygonStyle,
  IUnsavedLayerChanges,
} from "../../../../../../common/utils/layers/layerutils";

export default function CreateLayer({
  unsavedLayerChanges,
  setUnsavedLayerChanges,
}: {
  unsavedLayerChanges: IUnsavedLayerChanges;
  setUnsavedLayerChanges: React.Dispatch<
    React.SetStateAction<IUnsavedLayerChanges>
  >;
}) {
  const darkTheme = defaultStore((state) => state.darkTheme);

  const layerGroups = sidebarStore((state) => state.layerGroups);

  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const { t } = useTranslation();

  const [groupsList, setGroupsList] = useState<string[]>([]);

  const [showSettings, setShowSettings] = useState<boolean>(false);

  //change layer name
  const changeLayerName = (value: string) => {
    setUnsavedLayerChanges((prev) => ({
      ...prev,
      title: value,
    }));
  };

  //change layer group
  const changeGroup = (value: string) => {
    setUnsavedLayerChanges((prev) => ({
      ...prev,
      topic: value,
    }));
  };

  //change layer geometry type
  const changeGeometry = (value: mm.GEOMETRYTYPE) => {
    setUnsavedLayerChanges((prev) => ({
      ...prev,
      geometrytype: value,
    }));
  };

  //show layer settings
  const handleSettings = () => {
    setShowSettings(true);
  };

  //set different styles for different types
  useEffect(() => {
    if (
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.POINT ||
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.MULTIPOINT
    ) {
      setUnsavedLayerChanges((prev) => ({
        ...prev,
        style: defaultPointStyle,
      }));
    } else if (
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.LINESTRING ||
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.MULTILINESTRING
    ) {
      setUnsavedLayerChanges((prev) => ({
        ...prev,
        style: defaultLineStyle,
      }));
    } else if (
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.POLYGON ||
      unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.MULTIPOLYGON
    ) {
      setUnsavedLayerChanges((prev) => ({
        ...prev,
        style: defaultPolygonStyle,
      }));
    }
  }, [unsavedLayerChanges.geometrytype]);

  //save group list on a state
  useEffect(() => {
    setGroupsList(layerGroups);
  }, [layerGroups]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "calc(100% - 16px)",
        overflow: "auto",
        paddingY: 1,
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <TextField
          disabled={showSettings}
          color="primary"
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
          size="small"
          label={t("create_layer.name")}
          variant="outlined"
          onChange={(event) => changeLayerName(event.target.value)}
        />
        <Autocomplete
          disabled={showSettings}
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
          size="small"
          options={groupsList}
          noOptionsText={t("create_layer.group_helper")}
          getOptionLabel={(option) => option}
          onInputChange={(e, newValue) => {
            changeGroup(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("create_layer.group")}
              variant="outlined"
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  groupsList.findIndex(
                    (o) => o === unsavedLayerChanges.topic
                  ) === -1
                ) {
                  const newGroup = unsavedLayerChanges.topic;
                  if (newGroup && !groupsList.includes(newGroup)) {
                    setGroupsList((o) => o.concat(newGroup));
                  }
                }
              }}
            />
          )}
        />
        <FormControl
          size="small"
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
        >
          <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
          <Select
            disabled={showSettings}
            label="Geometry Type"
            defaultValue={""}
            onChange={(event) =>
              changeGeometry(Number(event.target.value) as mm.GEOMETRYTYPE)
            }
          >
            <MenuItem value={mm.GEOMETRYTYPE.POINT}>
              {t("create_layer.geometry_type_1")}
            </MenuItem>
            <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
              {t("create_layer.geometry_type_2")}
            </MenuItem>
            <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
              {t("create_layer.geometry_type_3")}
            </MenuItem>
          </Select>
        </FormControl>
        {!showSettings && (
          <Button
            disabled={
              unsavedLayerChanges.title === "" ||
              unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.UNDEFINED
            }
            sx={{
              borderColor: darkTheme ? "white" : "primary.main",
              color: darkTheme ? "white" : "primary.main",
            }}
            variant="outlined"
            onClick={() =>
              monitor(apm, "create-new-layer | Add", handleSettings)
            }
          >
            {t("manage_layers.button_add")}
          </Button>
        )}
      </Box>
      {showSettings && (
        <LayerSettings
          unsavedLayerChanges={unsavedLayerChanges}
          setUnsavedLayerChanges={setUnsavedLayerChanges}
          editProperties={true}
          editStyle={true}
          editPropertyDefDataType={true}
          canSaveCBF={(canSave: boolean) => {
            changeSaveButtonStateOnAddNewLayers(canSave);
          }}
        />
      )}
    </Box>
  );
}
